import * as React from 'react';
import { v4 as uuid } from 'uuid';
import { usePortalFeatures } from 'vet-bones/utils/portal';

import { SidebarSectionType } from 'src/components/sidebarSection/SidebarSection';
import { MAX_COMPETENCY_QUESTIONS } from 'src/constants/limits';
import { ProjectSidebarTileId } from 'src/constants/mosaic';
import { ProjectSidebarQuestionCard } from 'src/containers/ProjectSidebar/components/ProjectSidebarQuestionCard';
import { hasModelConceptsSelector } from 'src/selectors/hasModelConcepts';
import { projectSelector } from 'src/selectors/project';
import { projectCompetencyQuestionsSelector } from 'src/selectors/projectCompetencyQuestions';
import * as copy from 'src/templates/copy';
import { useIsVoiceboxEnabled } from 'src/utils/hooks/useIsVoiceboxEnabled';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';
import { useRouterParams } from 'src/utils/hooks/useRouterParams';
import { useEditQuestions } from 'src/utils/voicebox/hooks/useEditQuestions';
import { useEvaluateQuestions } from 'src/utils/voicebox/hooks/useEvaluateQuestions';

export const useQuestionsSection = (
  filter: string
): SidebarSectionType | null => {
  const { projectId } = useRouterParams();
  const project = useReduxSelector(projectSelector(projectId));

  const isVoiceboxEnabled = useIsVoiceboxEnabled();
  const portalFeatures = usePortalFeatures();
  const isVoiceboxSuggestionsEnabled = portalFeatures.voiceboxSuggestions;
  const showQuestions = isVoiceboxEnabled && isVoiceboxSuggestionsEnabled;

  const hasModelConcepts = useReduxSelector(
    hasModelConceptsSelector(projectId)
  );
  const questions = useReduxSelector(
    projectCompetencyQuestionsSelector(projectId)
  );
  const { evaluateQuestions, isLoadingEvaluations } = useEvaluateQuestions();
  const { editQuestionIdx, editQuestionValue, onEditQuestion } =
    useEditQuestions();

  const [isOpenByQuestionId, setIsOpenByQuestionId] = React.useState<
    Record<string, boolean>
  >({});

  const unevaluatedQuestions = questions.filter(
    (question) =>
      !question.evaluation ||
      !project?.voiceboxEvaluatedCompetencyQuestions?.includes(question.id)
  );

  const onAdd = React.useCallback(() => {
    onEditQuestion(questions.length, '');
  }, [onEditQuestion, questions.length]);

  const maybeOnRefresh = React.useCallback(() => {
    evaluateQuestions(unevaluatedQuestions);
  }, [evaluateQuestions, unevaluatedQuestions]);

  const onRefresh =
    hasModelConcepts && unevaluatedQuestions.length
      ? maybeOnRefresh
      : undefined;

  return React.useMemo(() => {
    if (!showQuestions) {
      return null;
    }

    const trimmed = filter.trim().toLowerCase();
    const items = questions
      .concat(
        editQuestionIdx === questions.length
          ? [{ id: uuid(), question: '' }]
          : []
      )
      .map((question, index) => ({ ...question, index }))
      .filter(
        ({ question }) => !trimmed || question.toLowerCase().includes(trimmed)
      )
      .map(({ index, ...question }) => (
        <ProjectSidebarQuestionCard
          key={index}
          editValue={editQuestionValue}
          idx={index}
          isEditing={editQuestionIdx === index}
          isOpen={isOpenByQuestionId[question.id]}
          onEditQuestion={onEditQuestion}
          question={question}
          questions={questions}
          setIsOpen={(isOpen: boolean) =>
            setIsOpenByQuestionId((prev) => ({
              ...prev,
              [question.id]: isOpen,
            }))
          }
        />
      ));

    return {
      id: ProjectSidebarTileId.COMPETENCY_QUESTIONS,
      addDisabled: questions.length >= MAX_COMPETENCY_QUESTIONS,
      addDisabledTooltip:
        copy.components.project.competencyQuestions.addQuestionDisabled,
      allCount: questions.length,
      refreshDisabled: isLoadingEvaluations,
      refreshTooltip: copy.components.project.competencyQuestions.evaluate,
      title: copy.components.project.competencyQuestions.title,
      onAdd,
      onRefresh,
      items,
      itemsCount: items.length,
    };
  }, [
    editQuestionIdx,
    editQuestionValue,
    filter,
    isLoadingEvaluations,
    isOpenByQuestionId,
    onAdd,
    onEditQuestion,
    onRefresh,
    questions,
    showQuestions,
  ]);
};
